import { useEffect, useState } from "react";
import { ChevronLeft, ShoppingBag, CreditCard, CheckCircle2, Truck, Loader2, ChevronRight, XCircle } from "lucide-react";
import { formatCurrency } from "@/config/locale";
import { AdImage } from "@/app/components/common/ad-image";
import { prefetchAdImageUrls } from "@/app/hooks/use-ad-image-url";
import { getOrdersList, type OrderSummaryApi } from "@/app/api/auth-client";
import { isStoragePath, normalizeAdImageStoragePath } from "@/app/utils/ad-utils";

interface PurchasesPageProps {
  onBack: () => void;
  onOpenOrderDetail: (orderId: string) => void;
}

/** Purchases list is buyer view: show "awaiting_second_payment" when buyer already paid on a swap. */
function displayStatus(order: OrderSummaryApi): string {
  if (
    order.status === "pending_payment" &&
    (order.deal_type || "").toLowerCase() === "swap" &&
    (order.first_payer_party || "").toLowerCase() === "buyer"
  ) {
    return "awaiting_second_payment";
  }
  return order.status;
}

function orderStatusToDisplay(status: string): string {
  switch (status) {
    case "pending_payment":
      return "Aguardando Pagamento";
    case "awaiting_second_payment":
      return "Aguardando pagamento da outra parte";
    case "paid":
    case "shipping_pending":
      return "Aguardando envio";
    case "in_delivery":
      return "Em entrega";
    case "delivered":
      return "Entregue";
    case "cancelled":
      return "Cancelado";
    default:
      return status.replace(/_/g, " ");
  }
}

function orderStatusColor(status: string): string {
  switch (status) {
    case "pending_payment":
    case "awaiting_second_payment":
      return "text-orange-600 bg-orange-50 dark:bg-orange-950/30";
    case "paid":
    case "shipping_pending":
      return "text-amber-600 bg-amber-50 dark:bg-amber-950/30";
    case "in_delivery":
      return "text-blue-600 bg-blue-50 dark:bg-blue-950/30";
    case "delivered":
      return "text-green-600 bg-green-50 dark:bg-green-950/30";
    case "cancelled":
      return "text-red-600 bg-red-50 dark:bg-red-950/30";
    default:
      return "text-gray-600 bg-gray-50";
  }
}

export function PurchasesPage({ onBack, onOpenOrderDetail }: PurchasesPageProps) {
  const [orders, setOrders] = useState<OrderSummaryApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getOrdersList()
      .then((res) => {
        if (cancelled) return;
        const list = res.orders ?? [];
        const paths = list
          .map((o) => normalizeAdImageStoragePath(o.ad_image ?? ""))
          .filter((p) => isStoragePath(p));
        prefetchAdImageUrls(paths);
        setOrders(list);
      })
      .catch(() => {
        if (!cancelled) setError("Não foi possível carregar os pedidos.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const totalSpent = orders
    .filter((o) => ["paid", "shipping_pending", "in_delivery", "delivered"].includes(o.status))
    .reduce((sum, o) => sum + (o.total_cents ?? 0), 0);
  const completedCount = orders.length; // list is all orders; could add "delivered" when we have it

  return (
    <div className="min-h-screen bg-background pb-20">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl text-white">Minhas Compras</h1>
              <p className="text-sm text-white/80">Acompanhe seus pedidos</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-10 h-10 text-primary animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-muted-foreground">{error}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Total Gasto</span>
                </div>
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(totalSpent / 100)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{orders.length} pedidos</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">Pedidos</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{completedCount}</p>
                <p className="text-xs text-muted-foreground mt-1">na lista</p>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="font-semibold text-lg">Histórico de Pedidos</h2>
              {orders.length === 0 ? (
                <div className="text-center py-16 bg-card border border-border rounded-lg">
                  <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <ShoppingBag className="w-10 h-10 text-muted-foreground" />
                  </div>
                  <h3 className="mb-2">Nenhuma compra ainda</h3>
                  <p className="text-muted-foreground text-sm">Seus pedidos aparecerão aqui</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div
                      key={order.order_id}
                      className="bg-card border border-border rounded-lg p-4 flex gap-4 cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => onOpenOrderDetail(order.order_id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onOpenOrderDetail(order.order_id);
                        }
                      }}
                    >
                      <AdImage
                        src={order.ad_image}
                        alt={order.ad_title || order.order_id}
                        className="w-20 h-20 object-cover rounded-lg shrink-0"
                      />
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          {(order.ad_title || (order.items_count != null && order.items_count > 1)) && (
                            <h3 className="font-medium text-sm line-clamp-1 mb-0.5">
                              {order.items_count != null && order.items_count > 1
                                ? `${order.ad_title || "Pedido"} e mais ${order.items_count - 1} ${order.items_count === 2 ? "produto" : "produtos"}`
                                : order.ad_title}
                            </h3>
                          )}
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm">#{order.order_id}</span>
                            <span
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${orderStatusColor(displayStatus(order))}`}
                            >
                              {order.status === "cancelled" ? (
                                <XCircle className="w-3 h-3" />
                              ) : (
                                <Truck className="w-3 h-3" />
                              )}
                              {orderStatusToDisplay(displayStatus(order))}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString("pt-BR", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </p>
                          <p className="text-lg font-semibold text-primary mt-1">
                            {formatCurrency((order.total_cents ?? 0) / 100)}
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
